import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, LinearProgress, Typography, Paper, Grid, Chip, TextField } from '@mui/material'
import { Container, TypoGraph } from 'components'
import { getUnderwritersScoreHeaders, getUnderwritersScoreRiskScore } from 'api'
import { isCarrierLogin } from 'tools/auth'
import { reportException } from 'tools/logs'
import { formatDate, formatNumber, formatKm, formatDateTimeNow } from 'tools/helper'
import { HeaderData, RiskScoreData } from 'interfaces'
import strings from 'strings'
import styles from './nbitelematics.module.scss'
import EstimatedMiles from './partial/estimatedMilesTotal'
import SegmentedProgressSteps from 'components/segmentedProgressSteps'

const t = strings.NBI_TELEMATICS

interface INBITelematicsProps {
  print?: boolean
  showRiskBadges?: boolean
}

const NBITelematics: React.FC<INBITelematicsProps> = (props) => {
  const { print, showRiskBadges = false } = props
  const [headerData, setHeaderData] = useState<HeaderData | null>(null)
  const [riskScoreData, setRiskScoreData] = useState<RiskScoreData | null>(null)
  const [isLoadingHeaders, setIsLoadingHeaders] = useState(true)
  const [isLoadingRiskScore, setIsLoadingRiskScore] = useState(true)
  const [policyCount, setPolicyCount] = useState<number>(1)

  const carrierId: any = isCarrierLogin()
    ? localStorage.getItem('companyId')
    : localStorage.getItem('carrierId') || '1'

  useEffect(() => {
    // Fetch both endpoints in parallel
    const fetchData = async () => {
      setIsLoadingHeaders(true)
      setIsLoadingRiskScore(true)

      try {
        const [headersResponse, riskScoreResponse] = await Promise.all([
          getUnderwritersScoreHeaders(carrierId),
          getUnderwritersScoreRiskScore(carrierId)
        ])

        setHeaderData(headersResponse as HeaderData)
        setRiskScoreData(riskScoreResponse as RiskScoreData)
      } catch (error) {
        reportException(error)
      } finally {
        setIsLoadingHeaders(false)
        setIsLoadingRiskScore(false)
      }
    }

    fetchData()
  }, [carrierId])

  useEffect(() => {
    if (headerData?.initialVehiclesWithinPolicy) {
      setPolicyCount(headerData.initialVehiclesWithinPolicy)
    }
  }, [headerData])

  const getRiskClassification = (score: number): {
    label: string
    color: string
    bgColor: string
  } => {
    if (!riskScoreData) return { label: '', color: '', bgColor: '' }

    const classifications = riskScoreData.underwritersScoreRiskClassification

    if (classifications.lowRisk && score >= classifications.lowRisk.lowerLimit && score <= classifications.lowRisk.upperLimit) {
      return { label: t.LOW_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
    }
    if (classifications.mediumRisk && score >= classifications.mediumRisk.lowerLimit && score <= classifications.mediumRisk.upperLimit) {
      return { label: t.MEDIUM_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
    }
    if (classifications.highRisk && score >= classifications.highRisk.lowerLimit && score <= classifications.highRisk.upperLimit) {
      return { label: t.HIGH_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
    }
    if (classifications.veryHighRisk && score >= classifications.veryHighRisk.lowerLimit && score <= classifications.veryHighRisk.upperLimit) {
      return { label: t.VERY_HIGH_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
    }
    return { label: t.VERY_HIGH_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
  }

  const handlePolicyCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10)
    if (!isNaN(value) && value > 0) {
      setPolicyCount(value)
    }
  }

  const calculatedAvgKmPerPolicy = (): string => {
    if (!headerData?.averageDistancePerMonth || !policyCount) return '-'
    const avgPerPolicy = headerData.averageDistancePerMonth / policyCount
    return formatKm(avgPerPolicy, t.KM_SUFFIX)
  }

  const calculateDaysOfData = (): number => {
    if (!headerData?.monthsOfData) return 0
    const endDate = new Date()
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - headerData.monthsOfData)

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  }

  const isLoading = isLoadingHeaders || isLoadingRiskScore

  const onboardedDate = localStorage.getItem('onboardingDate')
    ? formatDate(localStorage.getItem('onboardingDate') || '', false)
    : '-'

  const generatedDateTime = formatDateTimeNow()

  const riskScoreDate = riskScoreData?.riskScoreDate
    ? formatDate(riskScoreData.riskScoreDate, false)
    : '-'

  const finalRiskClassification = getRiskClassification(riskScoreData?.finalRiskScore || 0)
  const speedRiskClassification = getRiskClassification(riskScoreData?.speedRisk || 0)
  const mileageRiskClassification = getRiskClassification(riskScoreData?.mileageIndex || 0)
  const geographicRiskClassification = getRiskClassification(riskScoreData?.geographicIndex || 0)

  const riskSegmentRanges = React.useMemo(() => {
    const c = riskScoreData?.underwritersScoreRiskClassification
    if (!c) return null
    const segments: [number, number][] = []
    if (c.veryHighRisk) segments.push([c.veryHighRisk.lowerLimit, c.veryHighRisk.upperLimit])
    if (c.highRisk) segments.push([c.highRisk.lowerLimit, c.highRisk.upperLimit])
    if (c.mediumRisk) segments.push([c.mediumRisk.lowerLimit, c.mediumRisk.upperLimit])
    if (c.lowRisk) segments.push([c.lowRisk.lowerLimit, c.lowRisk.upperLimit])
    return segments.length > 0 ? segments : null
  }, [riskScoreData?.underwritersScoreRiskClassification])

  return (
    <Container data-testid="nbi-telematics-page">
      <Box sx={{ mt: 3 }} data-testid="nbi-telematics-content">
        <Paper
          elevation={0}
          className={styles.nbiTelematicsContainer__Score}
          sx={{
            p: 4,
            mb: 4,
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
          }}
          data-testid="nbi-telematics-score-section"
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pb: "16px",
              borderBottom: '1px solid #E0E0E0',
            }}
            data-testid="nbi-telematics-header-chips"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              {isLoadingHeaders ? (
                <Container sx={{ width: '125px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} data-testid="nbi-telematics-loading-active-vehicles">
                  <CircularProgress size={14} data-testid="nbi-telematics-spinner-active-vehicles" />
                </Container>
              ) : (
                <Chip
                  data-testid="nbi-telematics-value-active-vehicles"
                  label={`${t.ACTIVE_VEHICLES}: ${formatNumber(headerData?.activeVehicles)}`}
                  variant="filled"
                  sx={{
                    backgroundColor: '#E3E6E8',
                    color: '#1D2530',
                    fontSize: '1rem',
                    padding: '2px 10px',
                    width: 'auto',
                    height: '26px',
                    '& .MuiChip-label': {
                      padding: '0',
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      fontWeight: '600',
                    }
                  }}
                />
              )}
              {isLoadingHeaders ? (
                <Container sx={{ width: '125px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} data-testid="nbi-telematics-loading-onboarded">
                  <CircularProgress size={14} data-testid="nbi-telematics-spinner-onboarded" />
                </Container>
              ) : (
                <Chip
                  data-testid="nbi-telematics-value-onboarded"
                  label={`${t.ONBOARDED}: ${onboardedDate}`}
                  variant="filled"
                  sx={{
                    backgroundColor: '#E3E6E8',
                    color: '#1D2530',
                    fontSize: '1rem',
                    padding: '2px 10px',
                    width: 'auto',
                    height: '26px',
                    '& .MuiChip-label': {
                      padding: '0',
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      fontWeight: '600',
                    }
                  }}
                />
              )}
              {isLoadingHeaders ? (
                <Container sx={{ width: '125px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} data-testid="nbi-telematics-loading-months-of-data">
                  <CircularProgress size={14} data-testid="nbi-telematics-spinner-months-of-data" />
                </Container>
              ) : (
                <Chip
                  data-testid="nbi-telematics-value-months-of-data"
                  label={`${headerData?.monthsOfData || 0} ${t.MONTHS_OF_DATA}`}
                  variant="filled"
                  sx={{
                    backgroundColor: '#E3E6E8',
                    color: '#1D2530',
                    fontSize: '1rem',
                    padding: '2px 10px',
                    width: 'auto',
                    height: '26px',
                    '& .MuiChip-label': {
                      padding: '0',
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      fontWeight: '600',
                    }
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                data-testid="nbi-telematics-value-generated-on"
                label={`${t.GENERATED_ON} ${generatedDateTime}`}
                variant="outlined"
                sx={{
                  borderColor: 'rgb(217, 223, 232)',
                  fontSize: '1rem',
                  padding: '2px 10px',
                  width: 'auto',
                  height: '26px',
                  '& .MuiChip-label': {
                    padding: '0',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    fontWeight: '600',
                  }
                }}
              />
            </Box>
          </Box>

          <Grid container spacing={3} data-testid="nbi-telematics-metrics-grid">
            <Grid item xs={12} sm={6} md={2.4}>
              <Box data-testid="nbi-telematics-metric-avg-km-month">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.AVG_KM_MONTH}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }} data-testid="nbi-telematics-value-avg-km-month">
                  {isLoadingHeaders ? (
                    <CircularProgress size={24} data-testid="nbi-telematics-spinner-avg-km-month" />
                  ) : (
                    headerData?.averageDistancePerMonth
                      ? `${(headerData.averageDistancePerMonth / 1000).toFixed(2)}${t.K_SUFFIX}`
                      : '-'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box data-testid="nbi-telematics-metric-avg-active-vehicles-month">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.AVG_ACTIVE_VEHICLES_MONTH}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }} data-testid="nbi-telematics-value-avg-active-vehicles-month">
                  {isLoadingHeaders ? (
                    <CircularProgress size={24} data-testid="nbi-telematics-spinner-avg-active-vehicles" />
                  ) : (
                    formatNumber(headerData?.averageActiveVehiclesPerMonth)
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box data-testid="nbi-telematics-metric-avg-km-month-vehicle">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.AVG_KM_MONTH_VEHICLE}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }} data-testid="nbi-telematics-value-avg-km-month-vehicle">
                  {isLoadingHeaders ? (
                    <CircularProgress size={24} data-testid="nbi-telematics-spinner-avg-km-vehicle" />
                  ) : (
                    formatKm(headerData?.averageDistancePerMonthPerVehicle, t.KM_SUFFIX)
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box data-testid="nbi-telematics-metric-policy-count">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.POLICY_COUNT}
                </Typography>
                <TextField
                  data-testid="nbi-telematics-input-policy-count"
                  type="number"
                  value={policyCount}
                  onChange={handlePolicyCountChange}
                  disabled={isLoadingHeaders}
                  inputProps={{ min: 1, style: { textAlign: 'center' } }}
                  sx={{
                    width: '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      height: '48px',
                    },
                    '& input': {
                      fontWeight: 'bold',
                      fontSize: '1.5rem',
                      padding: '8px 12px',
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box data-testid="nbi-telematics-metric-avg-km-month-policy">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.AVG_KM_MONTH_POLICY}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }} data-testid="nbi-telematics-value-avg-km-month-policy">
                  {isLoadingHeaders ? (
                    <CircularProgress size={24} data-testid="nbi-telematics-spinner-avg-km-policy" />
                  ) : (
                    calculatedAvgKmPerPolicy()
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: '1px solid rgb(217, 223, 232)',
            }}
            data-testid="nbi-telematics-score-block"
          >
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }} data-testid="nbi-telematics-score-title">
              {t.NBI_TELEMATICS_SCORE_SECTION_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} data-testid="nbi-telematics-value-calculated-on-days">
              {t.CALCULATED_ON}{' '}
              <span data-testid="nbi-telematics-value-risk-score-date">{riskScoreDate}</span>
              {' '}• {t.CONTAINS}{' '}
              <span data-testid="nbi-telematics-value-days-of-data">{calculateDaysOfData() > 0 ? calculateDaysOfData() : '-'}</span>
              {' '}{t.DAYS_OF_DATA}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block', fontStyle: 'italic' }}>
              {t.MILEAGE_WEIGHTED_NOTE}
            </Typography>

            <Box sx={{ textAlign: 'center', mb: 2 }} data-testid="nbi-telematics-final-score-display">
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }} data-testid="nbi-telematics-score-value">
                  {t.NBI_TELEMATICS_SCORE_VALUE}
                </Typography>
                {showRiskBadges && (
                  <Chip
                    data-testid="nbi-telematics-value-final-risk-classification"
                    label={finalRiskClassification.label}
                    sx={{
                      backgroundColor: finalRiskClassification.bgColor,
                      color: finalRiskClassification.color,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>
              {isLoadingRiskScore ? (
                <CircularProgress size={40} data-testid="nbi-telematics-spinner-final-risk-score" />
              ) : (!isLoadingRiskScore && riskScoreData?.finalRiskScore != null && riskSegmentRanges) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }} data-testid="nbi-telematics-segmented-score">
                  <span data-testid="nbi-telematics-value-final-risk-score" aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}>{riskScoreData.finalRiskScore}</span>
                  <SegmentedProgressSteps
                    value={riskScoreData?.finalRiskScore}
                    ranges={riskSegmentRanges}
                    blockWidth={64}
                    blockHeight={48}
                  />
                </Box>
              ) : "-"}
            </Box>
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid rgb(217, 223, 232)',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t.WEIGHTED_FORMULA}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t.WEIGHTED_FORMULA_TEXT} {'='} <span data-testid="nbi-telematics-score-value-formula">{t.NBI_TELEMATICS_SCORE_VALUE}</span>
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Current Fleet Metrics Section */}
        <Box data-testid="nbi-telematics-current-fleet-metrics-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
            {t.CURRENT_FLEET_METRICS}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} data-testid="nbi-telematics-fleet-metrics-calculated-on">
            {t.CALCULATED_ON}{' '}
            <span data-testid="nbi-telematics-fleet-value-risk-score-date">{riskScoreDate}</span>
            {' '}• {t.CONTAINS}{' '}
            <span data-testid="nbi-telematics-fleet-value-days-of-data">{calculateDaysOfData() > 0 ? calculateDaysOfData() : '-'}</span>
            {' '}{t.DAYS_OF_DATA}
          </Typography>

          <Grid container spacing={2} data-testid="nbi-telematics-fleet-metrics-grid">
            {/* Speed Risk */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  height: '100%',
                }}
                data-testid="nbi-telematics-card-speed-index"
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: '600' }}>
                    {t.SPEED_INDEX}
                  </Typography>
                  {showRiskBadges && (
                    <Chip
                      data-testid="nbi-telematics-value-speed-risk-classification"
                      label={speedRiskClassification.label}
                      sx={{
                        backgroundColor: speedRiskClassification.bgColor,
                        color: speedRiskClassification.color,
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                </Box>
                {isLoadingRiskScore ? (
                  <CircularProgress size={30} sx={{ mx: 'auto', display: 'block' }} data-testid="nbi-telematics-spinner-speed-index" />
                ) : !isLoadingRiskScore && riskScoreData?.speedRisk != null && riskSegmentRanges ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }} data-testid="nbi-telematics-segmented-speed">
                    <span data-testid="nbi-telematics-value-speed-risk" aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}>{riskScoreData.speedRisk}</span>
                    <SegmentedProgressSteps
                      value={riskScoreData.speedRisk}
                      ranges={riskSegmentRanges}
                    />
                  </Box>
                ) : (
                  '-'
                )}
              <Box
                sx={{
                  mt: 2,
                  pt: 1,
                  borderTop: '1px solid rgb(217, 223, 232)',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t.SPEED_INDEX_DESCRIPTION}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Mileage Index */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                height: '100%',
              }}
              data-testid="nbi-telematics-card-mileage-index"
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
<Typography variant="body1" sx={{ fontWeight: '600' }}>
                {t.MILEAGE_INDEX}
              </Typography>
                {showRiskBadges && (
                  <Chip
                    data-testid="nbi-telematics-value-mileage-risk-classification"
                    label={mileageRiskClassification.label}
                    sx={{
                      backgroundColor: mileageRiskClassification.bgColor,
                      color: mileageRiskClassification.color,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>
{isLoadingRiskScore ? (
                  <CircularProgress size={30} sx={{ mx: 'auto', display: 'block' }} data-testid="nbi-telematics-spinner-mileage-index" />
                ) : !isLoadingRiskScore && riskScoreData?.mileageIndex != null && riskSegmentRanges ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }} data-testid="nbi-telematics-segmented-mileage">
                    <span data-testid="nbi-telematics-value-mileage-index" aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}>{riskScoreData.mileageIndex}</span>
                    <SegmentedProgressSteps
                      value={riskScoreData.mileageIndex}
                      ranges={riskSegmentRanges}
                    />
                  </Box>
              ) : (
                '-'
              )}
              <Box
                sx={{
                  mt: 2,
                  pt: 1,
                  borderTop: '1px solid rgb(217, 223, 232)',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t.MILEAGE_INDEX_DESCRIPTION}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Geographic Risk */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                height: '100%',
              }}
              data-testid="nbi-telematics-card-geographic-index"
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
<Typography variant="body1" sx={{ fontWeight: '600' }}>
                {t.GEOGRAPHIC_INDEX}
              </Typography>
                {showRiskBadges && (
                  <Chip
                    data-testid="nbi-telematics-value-geographic-risk-classification"
                    label={geographicRiskClassification.label}
                    sx={{
                      backgroundColor: geographicRiskClassification.bgColor,
                      color: geographicRiskClassification.color,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>
{isLoadingRiskScore ? (
                  <CircularProgress size={30} sx={{ mx: 'auto', display: 'block' }} data-testid="nbi-telematics-spinner-geographic-index" />
                ) : !isLoadingRiskScore && riskScoreData?.geographicIndex != null && riskSegmentRanges ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }} data-testid="nbi-telematics-segmented-geographic">
                    <span data-testid="nbi-telematics-value-geographic-index" aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}>{riskScoreData.geographicIndex}</span>
                    <SegmentedProgressSteps
                      value={riskScoreData.geographicIndex}
                      ranges={riskSegmentRanges}
                    />
                  </Box>
              ) : (
                '-'
              )}
              <Box
                sx={{
                  mt: 2,
                  pt: 1,
                  borderTop: '1px solid rgb(217, 223, 232)',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t.GEOGRAPHIC_INDEX_DESCRIPTION}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          </Grid>
        </Box>

        {/* Estimated Kilometers Section */}
        <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }} data-testid="nbi-telematics-estimated-kilometers-section">
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #E0E0E0',
              borderRadius: '12px',
              width: '100%'
            }}
            data-testid="nbi-telematics-estimated-kilometers-paper"
          >
            <EstimatedMiles useKilometers={true} />
          </Paper>
        </Box>
      </Box>

      {/* DISCLAIMER */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }} data-testid="nbi-telematics-disclaimer-section">
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #E0E0E0',
            borderRadius: '12px',
            backgroundColor: 'rgba(243, 245, 247, 0.5)'
          }}
          data-testid="nbi-telematics-disclaimer-paper"
        >
          <Typography variant="body2" color="text.secondary">
            <strong>{t.DISCLAIMER_LABEL}</strong> {t.DISCLAIMER_TEXT}
          </Typography>
        </Paper>
      </Box>
    </Container>
  )
}

export default NBITelematics

